import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Toast } from "./Toast";
import { ToastContext, type ToastValue } from "../hooks/useToast";

const DISMISS_MS = 5000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<{
    tone: "success" | "error";
    message: string;
  } | null>(null);
  const timer = useRef<number | null>(null);

  const clear = useCallback(() => {
    if (timer.current != null) window.clearTimeout(timer.current);
    timer.current = null;
    setToast(null);
  }, []);

  const show = useCallback((tone: "success" | "error", message: string) => {
    if (timer.current != null) window.clearTimeout(timer.current);
    setToast({ tone, message });
    timer.current = window.setTimeout(() => {
      timer.current = null;
      setToast(null);
    }, DISMISS_MS);
  }, []);

  const value = useMemo<ToastValue>(
    () => ({
      success: (message) => show("success", message),
      error: (message) => show("error", message),
    }),
    [show],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toast ? (
        <Toast tone={toast.tone} message={toast.message} onClose={clear} />
      ) : null}
    </ToastContext.Provider>
  );
}
